import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { X, Star } from "lucide-react";
import { useToast } from "../../contexts/ToastContext";
import * as offersService from "../../services/offersService";

function normalizeRatingErrorMessage(err, t) {
  const code = String(err?.code || "").toLowerCase();
  const raw = String(err?.message || "").toLowerCase();

  if (code.includes("unauthenticated")) {
    return t("inbox:ratingAuthRequired", "Please sign in and try again.");
  }

  if (code.includes("already-exists") || raw.includes("already rated")) {
    return t("inbox:ratingAlreadySubmitted", "You already submitted a rating for this transaction.");
  }

  if (code.includes("permission") || code.includes("failed-precondition") || raw.includes("deal must be completed")) {
    return t("inbox:ratingSubmitNotReady", "This transaction is not ready for rating yet.");
  }

  if (code.includes("unavailable") || code.includes("deadline-exceeded") || raw.includes("network")) {
    return t("inbox:ratingSubmitNetwork", "Your connection seems slow. Please try again in a moment.");
  }

  return t("inbox:ratingSubmitFriendly", "We couldn't submit your rating. Please try again.");
}

export default function DealRatingModal({ open, onClose, offer, currentUserId, chatId = null, defaultTargetLabel = "" }) {
  const { t } = useTranslation(["inbox", "common"]);
  const toast = useToast();
  const [stars, setStars] = useState(0);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState("");
  const [saving, setSaving] = useState(false);

  const targetUserId = useMemo(() => {
    if (!offer || !currentUserId) return null;
    const isSeller = String(currentUserId) === String(offer.sellerId);
    return isSeller ? offer.buyerId : offer.sellerId;
  }, [offer, currentUserId]);

  useEffect(() => {
    if (!open) return;
    setStars(0);
    setHover(0);
    setComment("");
    setSaving(false);
  }, [open, offer?.id]);

  if (!open || !offer) return null;

  const activeStar = hover || stars;

  const submitRating = async (e) => {
    e.preventDefault();
    if (!targetUserId || stars < 1 || saving) return;

    setSaving(true);
    try {
      await offersService.submitOfferRating({
        offerId: offer.id,
        fromUserId: currentUserId,
        chatId,
        stars,
        comment,
      });
      onClose?.(true);
      toast.success(t("inbox:ratingSubmitted", "Rating submitted."));
    } catch (err) {
      toast.error(
        normalizeRatingErrorMessage(err, t),
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center px-4">
      <button
        type="button"
        className="absolute inset-0 bg-black/50"
        aria-label={t("common:close", "Close")}
        onClick={() => onClose?.(false)}
      />

      <form
        onSubmit={submitRating}
        className="relative w-full max-w-md rounded-2xl border border-gray-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 shadow-2xl p-5"
      >
        <button
          type="button"
          onClick={() => onClose?.(false)}
          className="absolute top-3 right-3 p-1 rounded hover:bg-gray-100 dark:hover:bg-neutral-800"
          aria-label={t("common:close", "Close")}
        >
          <X className="w-4 h-4" />
        </button>

        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
          {t("inbox:rateTransaction", "Rate this transaction")}
        </h3>

        <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
          {t("inbox:ratePartnerPrompt", "Share your experience with {{name}}.", {
            name: defaultTargetLabel || t("inbox:directMessage", "the other user"),
          })}
        </p>

        <div className="mt-4 flex items-center gap-1">
          {[1, 2, 3, 4, 5].map((n) => (
            <button
              key={n}
              type="button"
              onClick={() => setStars(n)}
              onMouseEnter={() => setHover(n)}
              onMouseLeave={() => setHover(0)}
              className="p-1"
              aria-label={`${n}`}
            >
              <Star
                className={`w-6 h-6 ${n <= activeStar ? "text-amber-500" : "text-gray-300 dark:text-neutral-600"}`}
                fill={n <= activeStar ? "currentColor" : "none"}
                strokeWidth={n <= activeStar ? 0 : 1.5}
              />
            </button>
          ))}
        </div>

        <textarea
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          rows={3}
          maxLength={300}
          placeholder={t("inbox:optionalMessage", "Optional message")}
          className="mt-3 w-full rounded-lg border border-gray-200 dark:border-neutral-700 bg-gray-50 dark:bg-neutral-800 text-gray-900 dark:text-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500/30"
        />

        <div className="mt-4 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={() => onClose?.(false)}
            className="px-4 py-2 rounded-lg border border-gray-300 dark:border-neutral-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-neutral-800 text-sm font-medium"
          >
            {t("common:cancel", "Cancel")}
          </button>
          <button
            type="submit"
            disabled={saving || stars < 1}
            className="px-4 py-2 rounded-lg bg-green-600 hover:bg-green-700 text-white text-sm font-medium disabled:opacity-50"
          >
            {saving ? t("common:loading", "Loading...") : t("inbox:submitRating", "Submit rating")}
          </button>
        </div>
      </form>
    </div>
  );
}
